import { PLACE_ID_PATTERN } from '@bandido/shared/types';
import type { PlaceCoords, PlaceCoordsLookup } from '@bandido/shared/types';

import type { PlaceRef } from './places';
import { isRouteMapPoint } from './routeMap';

/**
 * Coordenadas de los lugares canónicos COMITEADOS, para el mapa del
 * recorrido (#2247). Nada de esto viaja al pedido: al search sigue yendo el
 * `plc_*` tal cual — Bandido nunca traduce un lugar a coordenadas, eso lo
 * hace rutero por conector (docs/PLACES.md §1).
 *
 * Dos fuentes, en este orden:
 *
 * 1. **La selección VIVA del formulario** (`PlaceRef.coords`, que trae el
 *    autocomplete). Es un ATAJO: dibuja el mapa sin esperar un roundtrip,
 *    pero solo existe mientras la pantalla no se rearme, y una opción ANIDADA
 *    del picker (un aeropuerto colgando de su ciudad) viaja sin coordenadas
 *    porque el contrato del maestro no las publica para los hijos.
 * 2. **La resolución por id** (`GET /places/:id/coords`), que es la fuente
 *    que cubre TODOS los casos: una búsqueda reconstruida desde la URL o
 *    desde recientes solo conserva el `plc_*`. El contrato del maestro
 *    declara que son las MISMAS coordenadas que devuelve el autocomplete, así
 *    que el orden no cambia qué se dibuja — solo cuándo.
 *
 * El atajo se usa SOLO si la selección viva sigue siendo la del pedido
 * (`selection.id === id`): si el asesor cambió el punto después de buscar, el
 * mapa se queda con el del pedido o no se dibuja, nunca con el de otra
 * selección (21.23.4).
 */
export interface CommittedPlaceCoordsSource {
  /** El `plc_*` que quedó en el pedido; sin él no hay nada que resolver. */
  id?: string;
  /** La selección viva del formulario, sea o no la comiteada. */
  selection?: PlaceRef | null;
  /** Lo que devolvió `GET /places/:id/coords` para ESE id. */
  resolved?: PlaceCoordsLookup;
}

/**
 * El `plc_*` de una referencia comiteada, o `undefined` si no tiene esa forma
 * (rail nativo guarda el NOMBRE de la estación en el mismo parámetro).
 */
export function committedPlaceId(value: string | undefined): string | undefined {
  return value && PLACE_ID_PATTERN.test(value) ? value : undefined;
}

/** Coordenadas usables, o `undefined`. Un par roto no dibuja: descarta. */
function usableCoords(coords: PlaceCoords | undefined): PlaceCoords | undefined {
  return coords && isRouteMapPoint(coords)
    ? { lat: coords.lat, lng: coords.lng }
    : undefined;
}

export function committedPlaceCoords({
  id,
  selection,
  resolved,
}: CommittedPlaceCoordsSource): PlaceCoords | undefined {
  if (!id) return undefined;
  const shortcut = selection?.id === id ? usableCoords(selection.coords) : undefined;
  // La respuesta trae el id que se PIDIÓ: si no es este, es de otro punto y
  // no se usa (el índice de abajo pide una por id, pero el chequeo vive acá
  // para que la regla no dependa de quién llame).
  const lookup = resolved?.id === id ? usableCoords(resolved.coords) : undefined;
  return shortcut ?? lookup;
}

/**
 * Índice `plc_* → coords` de los puntos comiteados que se pudieron resolver.
 * Los que no, simplemente no están: el consumidor pregunta por el id del
 * pedido y no hay forma de que le vuelva la coordenada de otro lugar.
 */
export function committedPlaceCoordsIndex(
  sources: CommittedPlaceCoordsSource[],
): Record<string, PlaceCoords> {
  const index: Record<string, PlaceCoords> = {};
  for (const source of sources) {
    const coords = committedPlaceCoords(source);
    if (source.id && coords) index[source.id] = coords;
  }
  return index;
}
